/* @flow */
import * as React from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { useTheme } from 'react-jss';

// Styles
import styles from './HelpButton.style';
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';

// Utils
import messages from 'src/shared/app/help/l10n/helpL10n';
import { trackGAEvent } from 'src/shared/core/service/tracking/gaService';

// Components
import HelpPopover from './HelpPopover';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { IconButton, Tooltip } from '@mui/material';
import { useSelector } from 'react-redux';
import { isHelpButtonHidden } from 'src/shared/app/help/selector/uiHelpButtonSelectors';

const useStyles = createUseThemeStyles(styles);

type Props = {
  layoutClass?: string,
};

const HelpButton = ({ layoutClass }: Props) => {
  const classes = useStyles();
  const intl = useIntl();
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);
  const theme = useTheme();

  const isHidden = useSelector(isHelpButtonHidden);
  const [popoverOpened, setPopoverOpened] = React.useState<boolean>(false);

  const onClick = () => {
    trackGAEvent('Help', 'Click help', 'From floating button');
    setPopoverOpened(true);
  };

  if (isHidden) return null;

  return (
    <>
      <Tooltip title={intl.formatMessage(messages.tooltip)} arrow>
        <IconButton
          color="inherit"
          aria-label={intl.formatMessage(messages.openHelpWindow)}
          onClick={onClick}
          classes={{
            root: classNames(classes.root, layoutClass),
          }}
          sx={{
            backgroundColor: theme.colorPrimary,
            border: `1px solid ${theme.accentColor}`,
          }}
          ref={buttonRef}
        >
          <SupportAgentIcon className={classes.icon} />
        </IconButton>
      </Tooltip>

      <HelpPopover
        open={popoverOpened}
        anchorEl={buttonRef.current}
        onClose={() => setPopoverOpened(false)}
      />
    </>
  );
};

export default React.memo<Props>(HelpButton);
